import { useState, useEffect, useRef } from "react";
import { Check, ChevronRight } from "lucide-react";
import { services } from "../data/serviceData";
import { formatRand } from "../lib/invoice";
import { useTheme } from "../context/ThemeContext";

export default function ServicesGrid({ selectedServices, onToggle }) {
  const [hovered, setHovered] = useState(null);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll("#services .reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-20 sm:py-24 px-5 sm:px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className={`text-xs uppercase tracking-[0.2em] mb-3 font-mono font-semibold ${isDark ? "text-[#00F0FF]" : "text-brand-teal"}`}>
            What we fix
          </p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className={`font-display font-bold text-4xl lg:text-5xl tracking-tighter ${isDark ? "text-white" : "text-slate-900"}`}>
              Services Grid
            </h2>
            {selectedServices.length > 0 && (
              <p className={`text-sm ${isDark ? "text-[#8F9098]" : "text-slate-500"}`}>
                <span className={`font-semibold ${isDark ? "text-[#00F0FF]" : "text-brand-orange"}`}>
                  {selectedServices.length}
                </span>{" "}
                service{selectedServices.length > 1 ? "s" : ""} selected
              </p>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {services.map((svc, index) => {
            const selected = selectedServices.includes(svc.id);
            const isHovered = hovered === svc.id;
            const Icon = svc.Icon;

            return (
              <button
                key={svc.id}
                data-testid={`service-card-${svc.id}`}
                onClick={() => onToggle(svc.id)}
                onMouseEnter={() => setHovered(svc.id)}
                onMouseLeave={() => setHovered(null)}
                className={`reveal reveal-delay-${(index % 4) + 1} relative text-left p-5 sm:p-6 rounded-xl border transition-all duration-300 group ${
                  selected
                    ? isDark
                      ? "bg-[#00F0FF]/10 border-[#00F0FF]/50 shadow-[0_0_24px_rgba(0,240,255,0.2)]"
                      : "bg-brand-orange/5 border-brand-orange/40 shadow-[0_0_24px_rgba(249,115,22,0.12)]"
                    : isDark
                      ? "bg-[#141419] border-[#222226] hover:border-[#00F0FF]/30 hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(0,240,255,0.1)]"
                      : "bg-white border-slate-200 hover:border-brand-blue/30 hover:-translate-y-1 shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-card-hover)]"
                }`}
              >
                {/* Selected check */}
                {selected && (
                  <div className={`absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center ${isDark ? "bg-[#00F0FF]" : "bg-brand-orange"}`}>
                    <Check size={10} className="text-white" strokeWidth={3} />
                  </div>
                )}

                {/* Icon */}
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 transition-all duration-300 ${
                    selected
                      ? isDark ? "bg-[#00F0FF]/20" : "bg-brand-orange/10"
                      : isDark ? "bg-white/5 group-hover:bg-[#00F0FF]/10" : "bg-brand-teal/5 group-hover:bg-brand-teal/10"
                  }`}
                >
                  <Icon
                    size={20}
                    className={selected
                      ? isDark ? "text-[#00F0FF]" : "text-brand-orange"
                      : isDark ? "text-[#8F9098] group-hover:text-[#00F0FF]" : "text-brand-teal group-hover:text-brand-teal-dark"
                    }
                  />
                </div>

                {/* Name */}
                <h3 className={`font-display font-semibold text-sm mb-1 leading-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                  {svc.name}
                </h3>

                {/* Short desc */}
                <p className={`text-xs leading-relaxed mb-4 ${isDark ? "text-[#8F9098]" : "text-slate-500"}`}>
                  {svc.short}
                </p>

                {/* Price + duration */}
                <div className="flex items-center justify-between">
                  <span className={`font-mono text-xs font-semibold ${isDark ? "text-[#00F0FF]" : "text-brand-blue"}`}>
                    {formatRand(svc.priceMin)}–{formatRand(svc.priceMax)}
                  </span>
                  <span className="text-xs text-[#8F9098] flex items-center gap-1">
                    {svc.duration} min
                    <ChevronRight size={10} />
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Add to booking CTA */}
        {selectedServices.length > 0 && (
          <div className="mt-8 flex justify-center">
            <a
              href="#booking"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });
              }}
              className={`inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold text-sm transition-all ${isDark ? "bg-[#00F0FF] text-black shadow-[0_0_24px_rgba(0,240,255,0.35)] hover:bg-white" : "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-[0_4px_16px_rgba(249,115,22,0.35)] hover:shadow-[0_6px_24px_rgba(249,115,22,0.45)]"}`}
            >
              Continue to Booking
              <ChevronRight size={16} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
